/**
 * applyChartDesign.ts
 *
 * Applies a widget's ChartDesign overrides (#968 Part C) onto an
 * already-built ECharts option object. Deliberately generic and post-hoc
 * rather than threaded into each of the nine chart generators
 * (generators/line.ts, generators/bar.ts, ...): every generator already
 * produces a conventional `xAxis`/`yAxis` shape, so one merge here covers
 * every chart type without touching nine separate files, and stays correct
 * as new chart types are added. Charts with no axes (pie) are untouched.
 *
 * Only properties the user explicitly overrode are patched. A chart whose
 * design was never touched must render pixel-identical to before this
 * feature existed — the generators set no `splitLine`, so ECharts' own
 * defaults (gridlines on value axes, none on category axes) are in effect
 * across every deployed dashboard, and an unconditional patch would have
 * changed all of them. That is also why this takes the RAW `ChartDesign`
 * rather than the resolved one: resolution to design-system defaults is for
 * the editor's controls (chartDesign.ts); the render path needs to know
 * which properties are actually set.
 *
 * Pure and side-effect free: PlatinumGraphs.tsx calls this once, right
 * before handing the option to ReactECharts, the same spot it already
 * post-processes for `hideLegend`.
 */

import type { EChartsOption } from 'echarts'
import type {
  ChartAxisDesign,
  ChartBarColorsDesign,
  ChartDesign,
  ChartGridlinesDesign,
  ChartLegendDesign,
  ChartDataLabelsDesign,
} from '../types/gotypes'
import type { DataType } from '../components/platinum-graphs/types'
import {
  CHART_LEGEND_TOP,
  CHART_DATAZOOM_BOTTOM,
  CHART_DATAZOOM_HEIGHT,
  CHART_GRID_BOTTOM_WITH_ZOOM,
} from '../components/platinum-graphs/generators/helpers'
import { ruleMatches } from './tableConditionalFormat'

function dashStyleToEcharts(style: string | undefined): 'solid' | 'dashed' | 'dotted' | undefined {
  if (style === 'dashed' || style === 'dotted' || style === 'solid') return style
  return undefined
}

interface AxisPatch {
  axisLabel?: Record<string, unknown>
  nameTextStyle?: Record<string, unknown>
  splitLineStyle?: Record<string, unknown>
  name?: string
}

/** Builds a patch containing ONLY the explicitly-overridden properties. */
function buildAxisPatch(axis: ChartAxisDesign | undefined, gridlines: ChartGridlinesDesign | undefined): AxisPatch {
  const patch: AxisPatch = {}

  const axisLabel: Record<string, unknown> = {}
  if (axis?.label_font_size != null) axisLabel.fontSize = axis.label_font_size
  if (axis?.label_color != null) axisLabel.color = axis.label_color
  if (Object.keys(axisLabel).length > 0) patch.axisLabel = axisLabel

  if (axis?.title_color != null) patch.nameTextStyle = { color: axis.title_color }
  // Only set `name` when there is an explicit non-empty title — an unset
  // title must not clobber a name a generator already set.
  if (axis?.title_text) patch.name = axis.title_text

  const lineStyle: Record<string, unknown> = {}
  if (gridlines?.color != null) lineStyle.color = gridlines.color
  if (gridlines?.width != null) lineStyle.width = gridlines.width
  const dash = dashStyleToEcharts(gridlines?.dash_style ?? undefined)
  if (dash !== undefined) lineStyle.type = dash
  // Styling gridlines never toggles their visibility: `show` stays whatever
  // the generator/ECharts default says (value axes on, category axes off).
  if (Object.keys(lineStyle).length > 0) patch.splitLineStyle = lineStyle

  return patch
}

function isEmptyPatch(patch: AxisPatch): boolean {
  return !patch.axisLabel && !patch.nameTextStyle && !patch.splitLineStyle && patch.name === undefined
}

function mergeAxis(existing: any, patch: AxisPatch): any {
  if (Array.isArray(existing)) {
    return existing.map((entry) => mergeAxis(entry, patch))
  }
  const merged: any = { ...existing }
  if (patch.axisLabel) {
    merged.axisLabel = { ...(existing?.axisLabel || {}), ...patch.axisLabel }
  }
  if (patch.nameTextStyle) {
    merged.nameTextStyle = { ...(existing?.nameTextStyle || {}), ...patch.nameTextStyle }
  }
  if (patch.splitLineStyle) {
    merged.splitLine = {
      ...(existing?.splitLine || {}),
      lineStyle: { ...(existing?.splitLine?.lineStyle || {}), ...patch.splitLineStyle },
    }
  }
  if (patch.name !== undefined) {
    merged.name = patch.name
  }
  return merged
}

/**
 * Builds a legend patch containing ONLY the explicitly-overridden
 * properties (#1044). `position` resets every positional anchor ECharts
 * reads (top/bottom/left/right/orient) rather than setting just one, so
 * switching from the generator's default top placement to e.g. 'right'
 * doesn't leave a stale `top: 4` fighting the new anchor.
 */
function buildLegendPatch(legend: ChartLegendDesign | undefined): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  if (!legend) return patch

  if (legend.show != null) patch.show = legend.show

  if (legend.position != null) {
    patch.top = 'auto'
    patch.bottom = 'auto'
    patch.left = 'auto'
    patch.right = 'auto'
    switch (legend.position) {
      case 'top':
        patch.top = CHART_LEGEND_TOP
        patch.left = 'center'
        patch.orient = 'horizontal'
        break
      case 'bottom':
        // Explicit user choice — the dataZoom collision this can cause is
        // documented on ChartLegendDesign and is the user's call (#1044).
        patch.bottom = CHART_DATAZOOM_BOTTOM
        patch.left = 'center'
        patch.orient = 'horizontal'
        break
      case 'left':
        patch.left = 0
        patch.top = 'middle'
        patch.orient = 'vertical'
        break
      case 'right':
        patch.right = 0
        patch.top = 'middle'
        patch.orient = 'vertical'
        break
    }
  }

  // No swatch, text kept (Matt, 14 Oct): ECharts draws no icon for 'none',
  // and a zero item width stops it reserving the swatch's space.
  if (legend.show_symbol === false) {
    patch.icon = 'none'
    patch.itemWidth = 0
  }

  const textStyle: Record<string, unknown> = {}
  if (legend.font_size != null) textStyle.fontSize = legend.font_size
  if (legend.font_color != null) textStyle.color = legend.font_color
  if (Object.keys(textStyle).length > 0) patch.textStyle = textStyle

  return patch
}

function mergeLegend(existing: any, patch: Record<string, unknown>): any {
  if (Object.keys(patch).length === 0) return existing

  // The line chart's legend can be an ARRAY: two stacked scroll rows with
  // the series split between them (#1184). Object-spreading an array would
  // produce `{0: ..., 1: ...}` garbage, so:
  //  - a REPOSITION patch collapses the rows back into one legend carrying
  //    all the series — the two-row split is a top-band layout, and mapping
  //    the same anchor onto both rows would stack them on top of each other;
  //  - a style-only patch (font size/color, show) applies to each row.
  if (Array.isArray(existing)) {
    if ('orient' in patch) {
      const combined = {
        ...existing[0],
        data: existing.flatMap((entry) => entry?.data ?? []),
      }
      delete (combined as { top?: unknown }).top
      return mergeLegend(combined, patch)
    }
    return existing.map((entry) => mergeLegend(entry, patch))
  }

  const { textStyle, ...rest } = patch as { textStyle?: Record<string, unknown> } & Record<string, unknown>
  return {
    ...(existing || {}),
    ...rest,
    ...(textStyle ? { textStyle: { ...(existing?.textStyle || {}), ...textStyle } } : {}),
  }
}

/**
 * Fixed px inset that keeps the plot area clear of a vertical (left/right)
 * legend (#1044 follow-up). Every generator's `grid.left`/`grid.right` is a
 * small percentage (see line.ts etc.) sized for NO legend competing for that
 * edge — a vertical legend painted directly over the plotted lines without
 * this, confirmed in a live dashboard (11-series line chart). There is no
 * way to ask ECharts for the legend's actual rendered width ahead of layout
 * (it depends on the longest series name), so this is a heuristic generous
 * enough for typical series-name lengths, not a computed value.
 */
const CHART_LEGEND_SIDE_INSET = 160

/**
 * Builds a grid patch that makes room for a left/right legend (#1044
 * follow-up). Top/bottom legends don't need this: the generators already
 * reserve `CHART_GRID_TOP_WITH_LEGEND` for a top legend, and a bottom legend
 * is the documented dataZoom-collision tradeoff, not a grid-sizing problem.
 */
function buildGridPatchForLegend(legend: ChartLegendDesign | undefined): Record<string, unknown> {
  if (legend?.position === 'left') return { left: CHART_LEGEND_SIDE_INSET }
  if (legend?.position === 'right') return { right: CHART_LEGEND_SIDE_INSET }
  return {}
}

function mergeGrid(existing: any, patch: Record<string, unknown>): any {
  if (Object.keys(patch).length === 0) return existing
  return { ...(existing || {}), ...patch }
}

/**
 * Formats a data-label value per the widget's Data Type (#1044): 'freq'
 * shows a plain integer, 'colPct'/'rowPct' show a percentage. Values already
 * arrive normalized as fractions for percent types (see toNumber /
 * axisPercentFormatter in generators/helpers.ts) — multiply by 100 and
 * append '%', exactly what axisPercentFormatter does for the axis ticks
 * sitting next to these labels. 'auto' resolves to 'percent0' (0dp), the
 * same precision as axisPercentFormatter, so a label reads "42%" next to an
 * axis tick that also reads "40%" rather than a mismatched "41.6%".
 */
function buildDataLabelFormatter(
  format: 'integer' | 'percent0' | 'percent1' | 'percent2',
): (params: { value?: unknown }) => string {
  return (params) => {
    const raw = params?.value
    const value = typeof raw === 'number' ? raw : Array.isArray(raw) ? raw[raw.length - 1] : null
    if (typeof value !== 'number' || Number.isNaN(value)) return 'N/A'
    switch (format) {
      case 'integer':
        return value.toLocaleString()
      case 'percent0':
        return `${(value * 100).toFixed(0)}%`
      case 'percent1':
        return `${(value * 100).toFixed(1)}%`
      case 'percent2':
        return `${(value * 100).toFixed(2)}%`
    }
  }
}

/** Builds a series-label patch containing ONLY the explicitly-overridden properties (#1044). */
function buildDataLabelsPatch(
  dataLabels: ChartDataLabelsDesign | undefined,
  dataType: DataType | undefined,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  if (!dataLabels) return patch

  if (dataLabels.show != null) patch.show = dataLabels.show

  // Build the formatter whenever labels are being shown, OR a format was
  // explicitly picked — 'auto' is the dropdown's own default DISPLAY value,
  // so a widget saved with Show=true and Auto left untouched never gets an
  // explicit `number_format` override written (the same "already looks
  // selected" gap that made Show default to true everywhere — see
  // resolveDataLabels in chartDesign.ts). Requiring an explicit
  // number_format here left the formatter unset in that case, so ECharts
  // fell back to its own default (raw `String(value)`) — confirmed live: a
  // Column % chart printed "0.4164" instead of "42%" (#1044 follow-up).
  if (dataLabels.number_format != null || dataLabels.show === true) {
    const requested = dataLabels.number_format ?? 'auto'
    const effective =
      requested === 'auto'
        ? dataType === 'freq' || dataType == null
          ? 'integer'
          : 'percent0'
        : requested
    patch.formatter = buildDataLabelFormatter(effective)
  }

  return patch
}

function mergeSeriesLabel(series: any, patch: Record<string, unknown>): any {
  if (Object.keys(patch).length === 0) return series
  if (Array.isArray(series)) {
    return series.map((entry) => mergeSeriesLabel(entry, patch))
  }
  if (!series || typeof series !== 'object') return series
  return { ...series, label: { ...(series.label || {}), ...patch } }
}

/**
 * Axis-scale routing (#1178) — the one piece of logic here that is NOT keyed
 * by the x_axis/y_axis section: the min/max/major-unit controls target the
 * VALUE axis and the label-interval control targets the CATEGORY axis,
 * decided by reading `type` on the BUILT option. On column/line the value
 * axis is Y; on the horizontal bar types it is X — a section-keyed patch
 * would do nothing on three chart types. Storage is canonical instead
 * (value fields on y_axis, label_interval on x_axis; see chartDesign.ts).
 *
 * Two confusingly-similar ECharts properties, deliberately spelled out:
 *  - `interval` ON THE AXIS is the tick/gridline STEP — the major unit;
 *  - `axisLabel.interval` is how many CATEGORY LABELS are skipped between
 *    shown ones (stored user-facing as "every Nth", so N-1 here).
 */
interface AxisScalePatch {
  min?: number
  max?: number
  majorUnit?: number
  categoryLabelInterval?: number
}

function buildAxisScalePatch(design: ChartDesign): AxisScalePatch {
  const patch: AxisScalePatch = {}
  // Canonical home first, other section as a defensive fallback.
  const value = design.y_axis?.min != null || design.y_axis?.max != null || design.y_axis?.major_unit != null
    ? design.y_axis
    : design.x_axis
  if (value?.min != null) patch.min = value.min
  if (value?.max != null) patch.max = value.max
  if (value?.major_unit != null) patch.majorUnit = value.major_unit
  const labelInterval = design.x_axis?.label_interval ?? design.y_axis?.label_interval
  if (labelInterval != null) patch.categoryLabelInterval = Math.max(0, labelInterval - 1)
  return patch
}

function hasAxisScalePatch(patch: AxisScalePatch): boolean {
  return patch.min !== undefined || patch.max !== undefined
    || patch.majorUnit !== undefined || patch.categoryLabelInterval !== undefined
}

function applyAxisScale(axis: any, patch: AxisScalePatch): any {
  if (!axis) return axis
  if (Array.isArray(axis)) return axis.map((entry) => applyAxisScale(entry, patch))
  if (axis.type === 'value') {
    const merged: any = { ...axis }
    // An explicit minimum beats the generators' hardcoded `min: 0`; a
    // maximum below the data's top clips the chart, which is a legitimate
    // choice (#1178).
    if (patch.min !== undefined) merged.min = patch.min
    if (patch.max !== undefined) merged.max = patch.max
    if (patch.majorUnit !== undefined) merged.interval = patch.majorUnit
    return merged
  }
  if (axis.type === 'category' && patch.categoryLabelInterval !== undefined) {
    return { ...axis, axisLabel: { ...(axis.axisLabel || {}), interval: patch.categoryLabelInterval } }
  }
  return axis
}

// ---------------------------------------------------------------------------
// Bar-family settings (#1808): "Balanced around 0" and colour rules. Both
// read the PLOTTED values off the built option, after row/column selection,
// so they see exactly the bars on screen and never a base row.
// ---------------------------------------------------------------------------

/** A bar datum's number: plain, `{value}`-wrapped, or none. */
function datumValue(d: unknown): number | null {
  const raw = d && typeof d === 'object' && !Array.isArray(d) ? (d as { value?: unknown }).value : d
  return typeof raw === 'number' && !Number.isNaN(raw) ? raw : null
}

function barSeriesOf(options: EChartsOption): any[] {
  const series = (options as any).series
  const list = Array.isArray(series) ? series : series ? [series] : []
  return list.filter((s: any) => s && s.type === 'bar' && Array.isArray(s.data))
}

/**
 * The balanced axis limit (#1808): longest bar plus half a step, rounded up
 * to a whole step. A step is one unit on raw data and one percentage point
 * on percentage data, which charts hold as fractions — so −9 gives 10, 15
 * gives 16, a 15.3% bar (0.153) gives 0.16. Never below one step, so an
 * all-zero chart still has an axis.
 */
export function balancedAxisLimit(values: number[], dataType?: DataType): number {
  const perUnit = dataType === 'colPct' || dataType === 'rowPct' ? 100 : 1
  const step = 1 / perUnit
  const maxAbs = values.reduce((m, v) => Math.max(m, Math.abs(v)), 0)
  const steps = Math.max(1, Math.ceil((maxAbs + step / 2) / step - 1e-9))
  // Divide rather than multiply so 16 steps read 0.16, not 0.16000000000000003.
  return steps / perUnit
}

/**
 * Every plotted bar length. Stacked series contribute per category the sum of
 * their positive values and, separately, of their negative ones — the two
 * ends of a stack that runs both ways.
 */
function plottedBarLengths(series: any[]): number[] {
  const lengths: number[] = []
  const stacks = new Map<string, { pos: number[]; neg: number[] }>()
  for (const s of series) {
    if (s.stack == null) {
      for (const d of s.data) {
        const v = datumValue(d)
        if (v !== null) lengths.push(v)
      }
      continue
    }
    const key = String(s.stack)
    const stack = stacks.get(key) ?? { pos: [], neg: [] }
    stacks.set(key, stack)
    s.data.forEach((d: unknown, i: number) => {
      const v = datumValue(d)
      if (v === null) return
      if (v >= 0) stack.pos[i] = (stack.pos[i] ?? 0) + v
      else stack.neg[i] = (stack.neg[i] ?? 0) + v
    })
  }
  for (const { pos, neg } of stacks.values()) {
    for (const v of pos) if (v !== undefined) lengths.push(v)
    for (const v of neg) if (v !== undefined) lengths.push(v)
  }
  return lengths
}

function applyBalancedLimit(axis: any, limit: number): any {
  if (!axis) return axis
  if (Array.isArray(axis)) return axis.map((entry) => applyBalancedLimit(entry, limit))
  return axis.type === 'value' ? { ...axis, min: -limit, max: limit } : axis
}

function hasBarColorRules(barColors: ChartBarColorsDesign | undefined): barColors is ChartBarColorsDesign {
  return Boolean(barColors && ((barColors.rules?.length ?? 0) > 0 || barColors.default_color))
}

/**
 * The colour a bar of `value` takes (#1808): the first rule that matches and
 * carries a colour, else the default, else none (the bar keeps its colour).
 * Thresholds are in display units — percentages compare as 0-100, the same
 * scale as the table widget's rules (tableConditionalFormat.ts).
 */
function barColorFor(value: number, barColors: ChartBarColorsDesign, dataType?: DataType): string | undefined {
  const raw = dataType === 'colPct' || dataType === 'rowPct' ? value * 100 : value
  const display = Math.round(raw * 1e9) / 1e9
  for (const rule of barColors.rules || []) {
    if (rule.background_color && ruleMatches(display, rule)) return rule.background_color
  }
  return barColors.default_color || undefined
}

function colourBarSeries(series: any, barColors: ChartBarColorsDesign, dataType?: DataType): any {
  if (!series || series.type !== 'bar' || !Array.isArray(series.data)) return series
  return {
    ...series,
    data: series.data.map((d: unknown) => {
      const value = datumValue(d)
      if (value === null) return d
      const color = barColorFor(value, barColors, dataType)
      if (!color) return d
      if (d && typeof d === 'object') {
        const item = d as { itemStyle?: Record<string, unknown> }
        return { ...item, itemStyle: { ...(item.itemStyle || {}), color } }
      }
      return { value, itemStyle: { color } }
    }),
  }
}

/**
 * Returns a new options object with the design overrides merged in, or the
 * exact same object when there is nothing to apply. Charts without an
 * `xAxis`/`yAxis` key (pie, etc.) pass through unchanged for that axis;
 * charts with no `series` key likewise pass through for data labels. A
 * legend patch is the exception (#1172): bar/column emit no legend key, so
 * a legend is created for the patch to land on — see below.
 * `options` itself is never mutated.
 *
 * `dataType` is the widget's Data Type (freq/colPct/rowPct) — only needed to
 * resolve a data-label 'auto' number format (#1044); every other patch is
 * self-contained.
 */
export function applyChartDesign(
  options: EChartsOption,
  design: ChartDesign | undefined,
  dataType?: DataType,
): EChartsOption {
  if (!options || !design) return options

  const xPatch = buildAxisPatch(design.x_axis, design.gridlines)
  const yPatch = buildAxisPatch(design.y_axis, design.gridlines)
  const legendPatch = buildLegendPatch(design.legend)
  const gridPatch = buildGridPatchForLegend(design.legend)
  const dataLabelsPatch = buildDataLabelsPatch(design.data_labels, dataType)
  const axisScalePatch = buildAxisScalePatch(design)

  const hasLegendPatch = Object.keys(legendPatch).length > 0
  const hasGridPatch = Object.keys(gridPatch).length > 0
  const hasDataLabelsPatch = Object.keys(dataLabelsPatch).length > 0
  const hasScalePatch = hasAxisScalePatch(axisScalePatch)
  const wantsRangeSlider = (design.x_axis?.range_slider ?? design.y_axis?.range_slider) === true
  // #1808: canonical home y_axis, x_axis as the same defensive fallback the
  // scale fields use.
  const wantsBalanced = (design.y_axis?.balanced_zero ?? design.x_axis?.balanced_zero) === true
  const barColors = hasBarColorRules(design.bar_colors) ? design.bar_colors : undefined

  if (isEmptyPatch(xPatch) && isEmptyPatch(yPatch) && !hasLegendPatch && !hasGridPatch && !hasDataLabelsPatch && !hasScalePatch && !wantsRangeSlider && !wantsBalanced && !barColors) {
    return options
  }

  const next: any = { ...options }
  if ('xAxis' in options && !isEmptyPatch(xPatch)) {
    next.xAxis = mergeAxis((options as any).xAxis, xPatch)
  }
  if ('yAxis' in options && !isEmptyPatch(yPatch)) {
    next.yAxis = mergeAxis((options as any).yAxis, yPatch)
  }
  // #1178: routed by the built axis's `type`, AFTER the section-keyed merge
  // above so the scale lands on whatever those patches produced.
  if (hasScalePatch) {
    if ('xAxis' in options) next.xAxis = applyAxisScale(next.xAxis ?? (options as any).xAxis, axisScalePatch)
    if ('yAxis' in options) next.yAxis = applyAxisScale(next.yAxis ?? (options as any).yAxis, axisScalePatch)
  }
  // #1172: opt-in range slider — the dataZoom the line generator used to
  // ship unconditionally, now added only when the design asks for it, and
  // only over a category X axis (a value X axis means a horizontal bar
  // family, where a bottom slider would zoom values, not categories). The
  // grid bottom grows to the with-zoom footprint so the slider doesn't sit
  // on the axis labels.
  if (wantsRangeSlider) {
    const builtX = (next.xAxis ?? (options as any).xAxis)
    const xType = Array.isArray(builtX) ? builtX[0]?.type : builtX?.type
    if (xType === 'category') {
      next.dataZoom = [
        {
          type: 'slider',
          start: 0,
          end: 100,
          bottom: CHART_DATAZOOM_BOTTOM,
          height: CHART_DATAZOOM_HEIGHT,
          filterMode: 'filter',
        },
      ]
      next.grid = { ...((next.grid ?? (options as any).grid) || {}), bottom: CHART_GRID_BOTTOM_WITH_ZOOM }
    }
  }
  if (hasLegendPatch) {
    // #1172: bar and column emit NO legend key at all, so gating on
    // `'legend' in options` silently disabled every legend control on those
    // charts. When the generator made no legend, CREATE one for the patch to
    // land on, seeded with the same top-centred geometry chartLegend() gives
    // the other generators (a reposition patch resets every anchor anyway —
    // see buildLegendPatch). Charts that already carry a legend are merged
    // exactly as before.
    const existing = 'legend' in options
      ? (options as any).legend
      : { top: CHART_LEGEND_TOP, left: 'center', textStyle: { fontSize: 11 } }
    next.legend = mergeLegend(existing, legendPatch)
  }
  // Grid inset composes with the legend patch above rather than replacing
  // it — a left/right legend needs BOTH the reposition and the room.
  if ('grid' in options && hasGridPatch) {
    next.grid = mergeGrid((options as any).grid, gridPatch)
  }
  if ('series' in options && hasDataLabelsPatch) {
    next.series = mergeSeriesLabel((options as any).series, dataLabelsPatch)
  }
  // #1808: "Balanced around 0" — sized on the plotted bars every time the
  // option is built, so each refresh gets the right axis. Runs AFTER the
  // scale patch, so it beats an explicit min/max (the editor disables those
  // while it is on). Charts with no bar series are left alone.
  if (wantsBalanced) {
    const bars = barSeriesOf(options)
    if (bars.length > 0) {
      const limit = balancedAxisLimit(plottedBarLengths(bars), dataType)
      if ('xAxis' in options) next.xAxis = applyBalancedLimit(next.xAxis ?? (options as any).xAxis, limit)
      if ('yAxis' in options) next.yAxis = applyBalancedLimit(next.yAxis ?? (options as any).yAxis, limit)
    }
  }
  // #1808: colour bars by value. Applied last, over the generator's
  // per-category colours, so a rule or the default beats color_overrides.
  if (barColors && 'series' in options) {
    const series = next.series ?? (options as any).series
    next.series = Array.isArray(series)
      ? series.map((entry: any) => colourBarSeries(entry, barColors, dataType))
      : colourBarSeries(series, barColors, dataType)
  }
  return next
}
